import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  CompanyTypeOption,
  CompanyTypeOverride,
  createOverride,
  updateOverride,
} from "@/services/companyClassificationApi";

import { parseAliases, parseLinkedinIds, validateOverrideForm } from "./classification";

export interface OverrideDraft {
  id?: number;
  company_type?: string;
  name?: string | null;
  aliases?: string[];
  linkedin_company_ids?: number[];
  note?: string | null;
}

interface OverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  types: CompanyTypeOption[];
  draft: OverrideDraft | null;
  onSaved: (override: CompanyTypeOverride) => void;
}

export function OverrideDialog({ open, onOpenChange, types, draft, onSaved }: OverrideDialogProps) {
  const navigate = useNavigate();
  const [companyType, setCompanyType] = useState("");
  const [name, setName] = useState("");
  const [aliasesText, setAliasesText] = useState("");
  const [idsText, setIdsText] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCompanyType(draft?.company_type ?? "");
    setName(draft?.name ?? "");
    setAliasesText((draft?.aliases ?? []).join("\n"));
    setIdsText((draft?.linkedin_company_ids ?? []).join(", "));
    setNote(draft?.note ?? "");
    setError(null);
  }, [open, draft]);

  const handleSave = async () => {
    const validation = validateOverrideForm({ companyType, name, idsText });
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      company_type: companyType,
      name: name.trim() || null,
      aliases: parseAliases(aliasesText),
      linkedin_company_ids: parseLinkedinIds(idsText).ids,
      note: note.trim() || null,
    };
    try {
      const saved = draft?.id
        ? await updateOverride(draft.id, payload, navigate)
        : await createOverride(payload, navigate);
      onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{draft?.id ? "Modifier la correction" : "Nouvelle correction de type"}</DialogTitle>
          <DialogDescription>
            Prioritaire sur les signaux et l'industrie LinkedIn. Correspondance exacte sur le nom normalisé
            (accents, casse, formes juridiques ignorés) ou sur l'identifiant LinkedIn.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Type d'entreprise</Label>
            <Select value={companyType} onValueChange={setCompanyType}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un type" />
              </SelectTrigger>
              <SelectContent>
                {types
                  .filter((type) => type.value !== "other")
                  .map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="override-name">Nom</Label>
            <Input id="override-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Accenture" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="override-aliases">Alias (un par ligne)</Label>
            <Textarea
              id="override-aliases"
              rows={3}
              value={aliasesText}
              onChange={(e) => setAliasesText(e.target.value)}
              placeholder={"Accenture France\nAccenture Technology"}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="override-ids">Identifiants LinkedIn (ou URN)</Label>
            <Input
              id="override-ids"
              value={idsText}
              onChange={(e) => setIdsText(e.target.value)}
              placeholder="1033, urn:li:fsd_company:157240"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="override-note">Note</Label>
            <Textarea id="override-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
